import { Tooltip } from "@progress/kendo-react-tooltip";
import React, { useEffect, useState } from "react";
import { Menu, MenuItem, ProSidebar, SidebarContent, SidebarFooter, SidebarHeader } from "react-pro-sidebar";
import "react-pro-sidebar/dist/css/styles.css";
import { useDispatch, useSelector } from "react-redux";
import { matchPath } from "react-router";
import { Link } from "react-router-dom";
import { updateCurrUser } from "../../../actions/auth";
import { setFavouriteFilters } from "../../../actions/bookmarks";
import { setContentManagerDataDetails, setContentManagerDataView, setFileMetaData, setSelectedContentItem } from "../../../actions/contentManager";
import { setIsApprovalFilterSelected, setLastFilterId } from "../../../actions/projects";
import { getBookmarks } from "../../../components/Bookmarks/bookmarks.service";
import Chatbot from "../../../components/Chatbot/chatbot";
import { getNotificationsCount } from "../../../components/Notifications/notifications.service";
import PlanMyDayActions from "../../../components/TaskSidebar/PlanMyDayActions/PlanMyDayActions";
import TaskSidebar from "../../../components/TaskSidebar/TaskSidebar";
import { handleCloseSidebar } from "../../../components/Tasks/tasks.common";
import { getDefaultTaskValues, pendingApprovalsCount } from "../../../components/Tasks/tasks.service";
import TeamSideBar from "../../../components/TeamSideBar/TeamSideBar";
import { icon, label, link, quote, route } from "../../../config/";
import { RESPONSIVE_WIDTH, number, tooltip } from "../../../config/constants";
import config from "../../../env.config";
import { appendString } from "../../../utils/common";
import { getVersionNo } from "../../services/common.service";
import { getFavouriteFilters, getTeamSavedFilter } from "../../services/projects.service";
import { loadState } from "../../services/session.service";
import { subStrWithTooltip } from "../../validators/validator";
import TeamSettingSidebar from "../../../components/TeamSetting/TeamSettingSidebar/TeamSettingSidebar";
import UserProfileSidebar from "../Header/UserProfile/UserProfileSidebar/UserProfileSidebar";
import SidePannel from "./SidePannel";
import "./sidebar.scss";
import { updatesIsCollabCentralOpened } from "../../../actions/common";

const SIDEBAR_NAV_LABELS = {
  COLLAB: "Collab",
  VIEWS: "Views",
  VAULT: "Vault",
};

const SidebarNavItem = ({ iconContent, labelText, children, className = "", innerClassName = "sidebar-nav-item" }) => (
  <div className={`${innerClassName} ${className}`.trim()}>
    <span className='sidebar-nav-item__icon'>{iconContent}</span>
    <span className='sidebar-nav-item__label'>{labelText}</span>
    {children}
  </div>
);

const SideBar = ({ sendCollapse }) => {
  const [menuCollapse, setMenuCollapse] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [toggleItems, setToggleItems] = useState({ tasks: false, projects: false, queues: false, comment: false, approval: false, reminder: false });
  const hoverTimeout = React.useRef(null);
  const isCollabCentralOpened = useSelector((state) => state.common.isCollabCentralOpened);
  const { id, companyId, operationalTeamName, IsChatbotEnabled, name } = useSelector(
    (state) => state.auth.user
  );
  const { showTaskSidebar, deferHeaderSidebarLoading, task } = useSelector((state) => state.taskSidebar);
  const latestSidebar = useSelector((state) => state.latestSidebar);
  const { showQueueSidebar } = useSelector((state) => state.teamSidebar);
  const { showUserProfileSidebar } = useSelector((state) => state.userProfileSidebar);
  const { showTeamSettingSidebar } = useSelector((state) => state.teamSettingSidebar);
  const {
    commentCount,
    reminderCount,
    approvalCount,
    showApprovalIcon,
    pendingApprovalsCount: pendingApprovalsFromRedux,
  } = useSelector((state) => state.notifications);
  const dispatch = useDispatch();
  const [version, setVersion] = useState();
  const homeRef = React.useRef();
  const projectRef = React.useRef();
  const queueRef = React.useRef();
  const filterRef = React.useRef();
  const libraryRef = React.useRef();
  const vaultRef = React.useRef();
  let pathname = window.location.pathname;
  const isTaskSidebarRoute = pathname === route.PRIVATE_ROUTE.TASK_SIDEBAR.PATH;
  const projectMatchPath = matchPath(pathname, { path: route.PRIVATE_ROUTE.TASKS.ROUTER_PATH });
  const queueMatchPath = matchPath(pathname, { path: route.PRIVATE_ROUTE.QUEUE_TASKS.ROUTER_PATH });
  const queueId = queueMatchPath?.params?.queueId;
  const projectId = projectMatchPath?.params?.projectId;
  const { isPlanMyDayActive } = useSelector((state) => state.planMyDay);
  const { teamSavedFilters, lastFilterId } = useSelector((state) => state.teamFilter);
  const { organisationPopUpShow } = useSelector((state) => state.auth.user);
  const { tenantAppUrl } = useSelector((state) => state.app);
  const user = useSelector((state) => state.auth.user);
  const { defaultDetails } = useSelector((state) => state.tasks);

    /** One fetch for privacy/tags defaults — avoids N cards each dispatching getDefaultTaskValues. */
    useEffect(() => {
      if (!defaultDetails?.isAdded) {
        dispatch(getDefaultTaskValues(user.companyId, user.id));
      }
    }, [ user.companyId, user.id, defaultDetails?.isAdded]);

  useEffect(() => {
    const storedVersion = loadState("version").length ? loadState("version") : "";
    if (version !== storedVersion) {
      setVersion(storedVersion);
    }
  });

  useEffect(() => {
    if (queueId) {
      setToggleItems({ home: false, projects: false, queues: true, comment: false, approval: false, reminder: false  , filters: false });
    } else if (projectId) {
      setToggleItems({ home: false, projects: true, queues: false, comment: false, approval: false, reminder: false, filters: false });
    }
  }, [queueId, projectId]);

  /** Loads the user's favourite filters into Redux for the sidebar hover panel.
   *  Lives in a wrapper so both bookmark-loading effects below can share it. */
  const loadFavouriteFilters = async () => {
    const favourites = await dispatch(getFavouriteFilters());
    dispatch(setFavouriteFilters(favourites || []));
  };

  useEffect(() => {
    if (!isTaskSidebarRoute) { 
      (async () => {
        dispatch(getBookmarks(id));
        loadFavouriteFilters();
        dispatch(setIsApprovalFilterSelected(false));
        dispatch(getVersionNo());
      })();
    }
  }, []);

  useEffect(() => {
    if (deferHeaderSidebarLoading || (isTaskSidebarRoute && !task?.taskId) ) {
      return;
    }
    if(isTaskSidebarRoute) { 
      (async () => {
        dispatch(getBookmarks(id));
        loadFavouriteFilters();
        dispatch(setIsApprovalFilterSelected(false));
        dispatch(getVersionNo());
      })();
    }
  }, [deferHeaderSidebarLoading, isTaskSidebarRoute, task?.taskId]);

  /** Set home as active by default on tasks route if no queue/project ID or on landing page*/
  useEffect(() => {
    (async () => {
      const isOnTasksRoute = pathname.startsWith(route.PRIVATE_ROUTE.TASKS.PATH);
      const isOnLandingPage = pathname === route.PRIVATE_ROUTE.LANDING_PAGE.PATH;
      const lastFilter = teamSavedFilters?.find((filter) => filter.value === lastFilterId);
      if (pathname === route.PRIVATE_ROUTE.TASKS.PATH && lastFilter?.label === label.PENDING_APPROVALS) {
        setToggleItems({
          home: false,
          projects: false,
          queues: false,
          comment: false,
          approval: true,
          reminder: false,
          library: false,
          filters: false,
        });
      }
      if ((lastFilter?.label !== label.PENDING_APPROVALS && isOnTasksRoute && !queueId && !projectId) || isOnLandingPage) {
        setToggleItems({
          home: true,
          projects: false,
          queues: false,
          comment: false,
          approval: false,
          reminder: false,
          library: false,
          filters: false,
        });
      }
    })();
  }, [pathname, queueId, projectId, lastFilterId]);

  window.onresize = function () {
    resizeActions();
  };
  window.onload = function () {
    resizeActions();
  };
  /**
   * Window Resize Actions for responsive sidebar
   */
  const resizeActions = () => {
    if (window.innerWidth <= RESPONSIVE_WIDTH) menuIconClick(true);
    else menuIconClick(false);
  };

  const handleOrganisationClick = async () => {
    await dispatch(updateCurrUser({ organisationPopUpShow: !organisationPopUpShow }));
  };

  const handleMouseEnter = (item) => {
    clearTimeout(hoverTimeout.current);
    setHoveredItem(item);
  };

  const handleMouseLeave = () => {
    hoverTimeout.current = setTimeout(() => setHoveredItem(null), 100);
  };

  /**
   * handling the open/close of sidebar
   * @returns {boolean to open/close sidebar}
   */
  const menuIconClick = (menu) => {
    setMenuCollapse(menu);
    sendCollapse(menu);
  };

  const toggleMenu = async (item) => {
    let items = toggleItems;
    dispatch(getNotificationsCount(id));
    items = Object.fromEntries(Object.keys(items).map((key) => [key, false]));
    items[item] = true;
    setToggleItems(items);
    if (item === "approval") {
      await dispatch(updateCurrUser({ lastSelectedBoard: label.GRID }));
      dispatch(setIsApprovalFilterSelected(true));
      const approvalFilter = teamSavedFilters?.find((filter) => filter.label === label.PENDING_APPROVALS);
      dispatch(setLastFilterId(approvalFilter?.value));
      return;
    }
    dispatch(setIsApprovalFilterSelected(false));
    if (item === "tasks") {
      const lastFilter = teamSavedFilters?.find((filter) => filter.value === lastFilterId);
      if (lastFilter?.label === label.PENDING_APPROVALS) {
        await dispatch(getTeamSavedFilter({ projectId: user?.myProjectId }));
      }
      dispatch(updateCurrUser({ lastSelectedBoard: label.GRID }));
    }
    if (item == label.LIBRARY_ROUTE && !pathname.includes(label.LIBRARY_ROUTE)) {
      dispatch(updateCurrUser({ mode: number.TWO }));
      dispatch(setSelectedContentItem([]));
      dispatch(setFileMetaData({}));
      dispatch(setContentManagerDataView(label.CMS_GRID));
      dispatch(setContentManagerDataDetails([]));
    }
  };

  /**
   * returns html element with tooltip if operationalTeamName is long
   * else returns String
   * @params
   * @author Pragun Gandotra
   */
  const getTeamLabel = () => {
    if (operationalTeamName) return subStrWithTooltip(appendString(operationalTeamName, label.SMALL_S), number.ZERO, number.TEN);
    return label.TEAM;
  };

  const handleShowCollabCentral = () => {
    dispatch(updatesIsCollabCentralOpened(true));
  };

  const handleFiltersSidebarClick = () => {
    toggleMenu("filters");
    handleCloseSidebar(dispatch, { isNewTask: false });
  };

  const handleTeamsSidebarClick = () => {
    toggleMenu("projects");
    handleCloseSidebar(dispatch, { isNewTask: false });
  };

  return (
    <>
      <ProSidebar collapsed={menuCollapse} className='vertical-menu position-fixed'>
        <SidebarHeader className={menuCollapse ? "menu-collapse justify-content-center" : "menu-expand justify-content-center border-none"}>
          <div
            onClick={() => {
              toggleMenu("home");
              handleCloseSidebar(dispatch, { isNewTask: false });
            }}
            className=' mt-2 company-logo-margin tour-landing-page'>
            <Link to={route.PRIVATE_ROUTE.LANDING_PAGE.PATH}>
              <div
                className={toggleItems.home ? "active sidebar-header-nav overflow-hidden cursor-pointer border-none " : "sidebar-header-nav overflow-hidden cursor-pointer border-none "}
                ref={homeRef}
                onMouseOver={() => handleMouseEnter("home")}
                onMouseLeave={handleMouseLeave}>
                <span className='sidebar-nav-item__icon'>{icon.BOARD_DIRECTORY_APP}</span>
                <span className='sidebar-header-nav__label'>{label.HOME}</span>
              </div>
              {hoveredItem === "home" && (
                <SidePannel
                  anchor={homeRef}
                  type={hoveredItem}
                  setType={setHoveredItem}
                  onPanelEnter={handleMouseEnter}
                  onPanelLeave={handleMouseLeave}
                />
              )}
            </Link>
          </div>
          <button
            className='btn btn-sm px-3 collapse-menu-icon position-absolute'
            id='button-sidebar-collapse'
            onClick={() => menuIconClick(!menuCollapse)}>
            <span className='menu-bar-icon'>{icon.MENU}</span>
            <span className='menu-close-icon'>{icon.CLOSE}</span>
          </button>
        </SidebarHeader>
        <div className='h-100 overflow-auto d-flex flex-column  overflow-y-auto sidebar-scrollbar'>
          <SidebarContent>
            <Menu>
              <MenuItem
                id='collab-central-sidebar'
                onClick={handleShowCollabCentral}
                className={isCollabCentralOpened ? "active" : ""}>
                <SidebarNavItem iconContent={icon.COLLAB_CENTRAL_APP} labelText={SIDEBAR_NAV_LABELS.COLLAB} />
              </MenuItem>

              <Link id='filters-sidebar' to={`${route.PRIVATE_ROUTE.USER_PROFILE_SETTING.PATH}/filters`}>
                <MenuItem
                  onClick={handleFiltersSidebarClick}
                  ref={filterRef}
                  onMouseEnter={() => handleMouseEnter("filters")}
                  onMouseLeave={handleMouseLeave}
                  className={toggleItems.filters ? "active" : ""}>
                  <SidebarNavItem iconContent={icon.FILTER_ICON} labelText={SIDEBAR_NAV_LABELS.VIEWS} />
                  {hoveredItem === "filters" && (
                    <SidePannel
                      anchor={filterRef}
                      type={hoveredItem}
                      setType={setHoveredItem}
                      onPanelEnter={handleMouseEnter}
                      onPanelLeave={handleMouseLeave}
                    />
                  )}
                </MenuItem>
              </Link>
              <Link
                id='team-sidebar'
                to={`${route.PRIVATE_ROUTE.USER_PROFILE_SETTING.PATH}${route.PRIVATE_ROUTE.USER_PROFILE_SETTING.OVERVIEW_PATH}`}>
                <MenuItem
                  onClick={handleTeamsSidebarClick}
                  ref={projectRef}
                  onMouseEnter={() => handleMouseEnter("projects")}
                  onMouseLeave={handleMouseLeave}
                  className={toggleItems.projects ? "active" : ""}>
                  <SidebarNavItem iconContent={icon.MY_TEAM_SIDEBAR} labelText={getTeamLabel()} />
                  {hoveredItem === "projects" && (
                    <SidePannel
                      anchor={projectRef}
                      type={hoveredItem}
                      setType={setHoveredItem}
                      onPanelEnter={handleMouseEnter}
                      onPanelLeave={handleMouseLeave}
                    />
                  )}
                </MenuItem>
              </Link>

              <Link id='queue-sidebar' to={route.PRIVATE_ROUTE.QUEUES.PATH}>
                <MenuItem
                  onClick={() => {
                    toggleMenu("queues");
                    handleCloseSidebar(dispatch, { isNewTask: false });
                  }}
                  ref={queueRef}
                  onMouseEnter={() => handleMouseEnter("queues")}
                  onMouseLeave={handleMouseLeave}
                  className={toggleItems.queues ? "active" : ""}>
                  <SidebarNavItem iconContent={icon.APP_WORKSPACE} labelText={label.SPACES} />
                  {hoveredItem === "queues" && (
                    <SidePannel
                      anchor={queueRef}
                      type={hoveredItem}
                      setType={setHoveredItem}
                      onPanelEnter={handleMouseEnter}
                      onPanelLeave={handleMouseLeave}
                    />
                  )}
                </MenuItem>
              </Link>

              <Link id='quick-access-sidebar' to={{ pathname: route.PRIVATE_ROUTE.CMS.PATH, state: { openQuickAccess: true } }}>
                <MenuItem
                  onClick={() => {
                    toggleMenu(label.QUICK_ACCESS_ROUTE);
                    handleCloseSidebar(dispatch, { isNewTask: false });
                  }}
                  onMouseEnter={() => handleMouseEnter(label.QUICK_ACCESS_ROUTE)}
                  onMouseLeave={handleMouseLeave}
                  className={toggleItems.quickAccess ? "active " : ""}>
                  <SidebarNavItem iconContent={icon.QUICK_ACCESS_APP} labelText={SIDEBAR_NAV_LABELS.VAULT} />
                </MenuItem>
              </Link>

              <Link id='library-sidebar' to={`${route.PRIVATE_ROUTE.CMS.PATH}`}>
                <MenuItem
                  onClick={() => {
                    toggleMenu(label.LIBRARY_ROUTE);
                    handleCloseSidebar(dispatch, { isNewTask: false });
                  }}
                  ref={libraryRef}
                  onMouseEnter={() => handleMouseEnter(label.LIBRARY_ROUTE)}
                  onMouseLeave={handleMouseLeave}
                  className={toggleItems.library ? "active " : ""}>
                  <SidebarNavItem iconContent={icon.APP_LIBRARY} labelText={label.LIBRARY} />
                  {hoveredItem === label.LIBRARY_ROUTE && (
                    <SidePannel
                      anchor={libraryRef}
                      type={hoveredItem}
                      setType={setHoveredItem}
                      onPanelEnter={handleMouseEnter}
                      onPanelLeave={handleMouseLeave}
                    />
                  )}
                </MenuItem>
              </Link>

              <Link id='comment-sidebar' to={`${route.PRIVATE_ROUTE.COMMENT.PATH}`}>
                <MenuItem
                  onClick={() => {
                    toggleMenu("comment");
                    handleCloseSidebar(dispatch, { isNewTask: false });
                  }}
                  className={toggleItems.comment ? "active " : ""}>
                  <SidebarNavItem
                    iconContent={icon.COMMENT_APP}
                    labelText={label.PING}
                    className='position-relative notifications-action-item'>
                    {commentCount > 0 && <span className='notification-dot'></span>}
                  </SidebarNavItem>
                </MenuItem>
              </Link>
            </Menu>
          </SidebarContent>
        </div>
        <SidebarFooter>
          <div className='text-center mb-3'></div>
          {!IsChatbotEnabled && (
            <div className='mb-2'>
              <a href={link.HELP_SUPPORT} className='head-set text-center' target='_blank' rel='noopener noreferrer'>
                <Tooltip anchorElement='target' parentTitle={true} position='right'>
                  <span>{icon.HEADSET}</span>
                </Tooltip>
              </a>
            </div>
          )}
          <div className='text-center'>
            <a className='help-text small' href={link.VERSION_URL} target='_blank' rel='noreferrer'>
              {quote.VERSION} {version}
            </a>
          </div>
          <div>
            {!!IsChatbotEnabled && (
              <Chatbot
                userId={id}
                organizationId={companyId}
                baseUrl={config.BASE_URL.BASE_API_URL}
                userName={name}
                domainName={tenantAppUrl ? tenantAppUrl : config.BASE_URL.APP_URL}
              />
            )}
          </div>
        </SidebarFooter>
      </ProSidebar>
      <div
        className={`task-slide-left position-fixed p-0 right-0  ${showTaskSidebar ? "page-slide-show" : ""} ${
          latestSidebar.tasks ? "top-sidebar" : ""
        }`}>
        {showTaskSidebar && <TaskSidebar />}
      </div>
      <div
        className={`task-slide-left position-fixed p-0 right-0 ${showQueueSidebar ? "page-slide-show team-right-sidebar" : ""} ${
          latestSidebar?.teams ? "top-sidebar" : ""
        }`}>
        {showQueueSidebar && <TeamSideBar />}
      </div>
      <div
        className={`task-slide-left position-fixed p-0 right-0  ${showUserProfileSidebar ? "page-slide-show" : ""} ${
          latestSidebar?.userProfile ? "top-sidebar" : ""
        }`}>
        {showUserProfileSidebar && <UserProfileSidebar />}
      </div>
      <div
        className={`task-slide-left position-fixed p-0 right-0  ${showTeamSettingSidebar ? "page-slide-show top-sidebar" : ""}`}>
        {showTeamSettingSidebar && <TeamSettingSidebar />}
      </div>
      {isPlanMyDayActive && <PlanMyDayActions />}
    </>
  );
};
export default SideBar;
